import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useAuth } from '../components/AuthContext';
import RichHtmlFrame from '../components/RichHtmlFrame';
import { isFullHtmlDocument, sanitizeRichHtml } from '../utils/promotionalBannerHtml';
import { useAdminPromotionalBannerConfig } from '../hooks/useApiQueries';
import { getAllFranchises, getFranchise } from '../utils';

interface FranchiseBanner {
  franchise_id: string;
  title: string;
  subtitle: string;
  content: string;
  footer_text: string;
}

const PromotionalBannerAdminPage = () => {
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useAdminPromotionalBannerConfig();

  const [isActive, setIsActive] = useState(false);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [content, setContent] = useState('');
  const [footerText, setFooterText] = useState('');
  const [borderColor, setBorderColor] = useState('#fdbb5f');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [textColor, setTextColor] = useState('#000000');
  const [excludedFranchises, setExcludedFranchises] = useState<string[]>([]);
  const [franchiseBanners, setFranchiseBanners] = useState<FranchiseBanner[]>([]);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const franchiseOptions = getAllFranchises();

  // Vérifier que l'utilisateur est god
  useEffect(() => {
    if (userRole && userRole !== 'god') {
      navigate('/');
    }
  }, [userRole, navigate]);

  // Charger les données du serveur
  useEffect(() => {
    if (data?.config) {
      setIsActive(data.config.is_active);
      setTitle(data.config.title || '');
      setSubtitle(data.config.subtitle || '');
      setContent(data.config.content || '');
      setFooterText(data.config.footer_text || '');
      setBorderColor(data.config.border_color || '#fdbb5f');
      setBackgroundColor(data.config.background_color || '#ffffff');
      setTextColor(data.config.text_color || '#000000');
      setExcludedFranchises(data.config.excluded_franchises || []);
      const rawBanners = data.config.franchise_banners || [];
      setFranchiseBanners(
        rawBanners.map((fb: Partial<FranchiseBanner> & { franchise_id: string }) => ({
          franchise_id: fb.franchise_id,
          title: fb.title || '',
          subtitle: fb.subtitle || '',
          content: fb.content || '',
          footer_text: fb.footer_text || '',
        })),
      );
    }
  }, [data]);

  const handleAddFranchise = () => {
    setExcludedFranchises([...excludedFranchises, '']);
  };

  const handleRemoveFranchise = (index: number) => {
    const newFranchises = excludedFranchises.filter((_, i) => i !== index);
    setExcludedFranchises(newFranchises);
  };

  const handleFranchiseChange = (index: number, value: string) => {
    const newFranchises = [...excludedFranchises];
    newFranchises[index] = value;
    setExcludedFranchises(newFranchises);
  };

  // Handlers pour les bannières par franchise
  const handleAddFranchiseBanner = () => {
    setFranchiseBanners([...franchiseBanners, { franchise_id: '', title: '', subtitle: '', content: '', footer_text: '' }]);
  };

  const handleRemoveFranchiseBanner = (index: number) => {
    setFranchiseBanners(franchiseBanners.filter((_, i) => i !== index));
  };

  const handleFranchiseBannerChange = (index: number, field: keyof FranchiseBanner, value: string) => {
    const updated = [...franchiseBanners];
    updated[index] = { ...updated[index], [field]: value };
    setFranchiseBanners(updated);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSuccessMessage('');
      setErrorMessage('');

      // Validation
      if (!title.trim()) {
        setErrorMessage('Le titre est obligatoire');
        setIsSaving(false);
        return;
      }

      // Filtrer les bannières franchise valides (au moins un franchise_id)
      const validFranchiseBanners = franchiseBanners.filter((fb) => fb.franchise_id.trim() !== '');

      const payload = {
        is_active: isActive,
        title: title.trim(),
        subtitle: subtitle.trim(),
        content: content.trim(),
        footer_text: footerText.trim(),
        border_color: borderColor,
        background_color: backgroundColor,
        text_color: textColor,
        excluded_franchises: excludedFranchises.filter((f) => f.trim() !== ''),
        franchise_banners: validFranchiseBanners,
      };

      await makeAuthenticatedApiCall('post', '/api/promotional-banner/admin/config', payload);

      // Invalider les caches pour forcer le rechargement
      queryClient.invalidateQueries({ queryKey: ['adminPromotionalBannerConfig'] });
      queryClient.invalidateQueries({ queryKey: ['promotionalBannerConfig'] });

      setSuccessMessage('Configuration sauvegardée avec succès !');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error: any) {
      console.error('Error saving config:', error);
      setErrorMessage(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper pour le rendu du contenu dans les prévisualisations
  const renderPreviewContent = (previewContent: string) => {
    if (!previewContent) return null;

    if (isFullHtmlDocument(previewContent)) {
      return <RichHtmlFrame html={sanitizeRichHtml(previewContent)} />;
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
        {previewContent
          .split('|')
          .filter((item) => item.trim())
          .map((item, idx) => (
            <Typography
              key={idx}
              variant="body1"
              sx={{ color: textColor, fontWeight: 600, textAlign: 'center', fontSize: '0.8rem' }}
            >
              {item.trim()}
            </Typography>
          ))}
      </Box>
    );
  };

  const renderPreviewFooter = (previewFooter: string) => {
    if (!previewFooter) return null;

    if (isFullHtmlDocument(previewFooter)) {
      return <RichHtmlFrame html={sanitizeRichHtml(previewFooter)} />;
    }

    return <Box sx={{ mt: 2 }}>{previewFooter}</Box>;
  };

  if (userRole !== 'god') {
    return null;
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Configuration de la Bannière Promotionnelle
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            {/* Toggle Active/Inactive */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />}
                label="Bannière active"
                className="inputElement"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Activer ou désactiver l'affichage de la bannière pour les agences
              </Typography>
            </Grid>

            {/* Titre */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titre (par défaut)"
                className="inputElement"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                helperText="Le titre principal de la bannière (obligatoire). Sera utilisé par défaut pour toutes les franchises."
              />
            </Grid>

            {/* Sous-titre */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sous-titre (par défaut)"
                className="inputElement"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                helperText="Le sous-titre de la bannière (optionnel)"
              />
            </Grid>

            {/* Contenu */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                className="inputElement"
                multiline
                rows={20}
                label="Contenu (par défaut)"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                helperText="Format simple: éléments séparés par '|' (ex: 1ère reprise → 50€|2ème reprise → 100€). Ou collez du HTML complet généré par une IA."
              />
            </Grid>

            {/* Texte de bas de page */}
            <Grid item xs={12}>
              <TextField
                className="inputElement"
                fullWidth
                multiline
                rows={20}
                label="Texte de bas de page - Conditions (par défaut)"
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                helperText="HTML simple (&lt;b&gt;, &lt;br&gt;) ou document HTML complet à coller."
              />
            </Grid>

            {/* Couleurs */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Couleur des bordures"
                  className="inputElement"
                  value={borderColor}
                  onChange={(e) => setBorderColor(e.target.value)}
                  helperText="Format hex (ex: #fdbb5f)"
                />
                <input
                  type="color"
                  className="inputElement"
                  value={borderColor}
                  onChange={(e) => setBorderColor(e.target.value)}
                  style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Couleur de fond"
                  className="inputElement"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  helperText="Format hex (ex: #ffffff)"
                />
                <input
                  type="color"
                  className="inputElement"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Couleur du texte"
                  value={textColor}
                  className="inputElement"
                  onChange={(e) => setTextColor(e.target.value)}
                  helperText="Format hex (ex: #000000)"
                />
                <input
                  type="color"
                  className="inputElement"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                />
              </Box>
            </Grid>

            {/* Bannières personnalisées par franchise */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Contenus personnalisés par franchise
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Ajoutez des contenus spécifiques pour certaines franchises. Les champs laissés vides utiliseront les
                valeurs par défaut ci-dessus.
              </Typography>

              {franchiseBanners.map((fb, index) => (
                <Card key={index} sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={11}>
                      <FormControl fullWidth className="inputElement">
                        <InputLabel id={`franchise-banner-label-${index}`}>Franchise</InputLabel>
                        <Select
                          labelId={`franchise-banner-label-${index}`}
                          label="Franchise"
                          value={fb.franchise_id}
                          onChange={(e) => handleFranchiseBannerChange(index, 'franchise_id', e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Sélectionner une franchise</em>
                          </MenuItem>
                          {franchiseOptions.map((f) => (
                            <MenuItem key={f.id} value={f.id}>
                              {f.id} - {f.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton onClick={() => handleRemoveFranchiseBanner(index)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Titre (laisser vide = titre par défaut)"
                        value={fb.title}
                        className="inputElement"
                        onChange={(e) => handleFranchiseBannerChange(index, 'title', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Sous-titre (laisser vide = sous-titre par défaut)"
                        value={fb.subtitle}
                        className="inputElement"
                        onChange={(e) => handleFranchiseBannerChange(index, 'subtitle', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Contenu (laisser vide = contenu par défaut)"
                        value={fb.content}
                        className="inputElement"
                        onChange={(e) => handleFranchiseBannerChange(index, 'content', e.target.value)}
                        helperText="Format simple (séparés par '|') ou HTML complet"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Texte de bas de page (laisser vide = footer par défaut)"
                        value={fb.footer_text}
                        className="inputElement"
                        onChange={(e) => handleFranchiseBannerChange(index, 'footer_text', e.target.value)}
                        helperText="HTML simple ou document HTML complet"
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}

              <Button
                className="secondaryDealActionButton"
                startIcon={<AddIcon />}
                onClick={handleAddFranchiseBanner}
                variant="outlined"
              >
                Ajouter une bannière franchise
              </Button>
            </Grid>

            {/* Franchises exclues */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Franchises exclues
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Ces franchises ne verront aucune bannière (ni la bannière par défaut, ni une bannière personnalisée)
              </Typography>

              {excludedFranchises.map((franchise, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <FormControl fullWidth className="inputElement">
                    <InputLabel id={`excluded-franchise-label-${index}`}>Franchise {index + 1}</InputLabel>
                    <Select
                      labelId={`excluded-franchise-label-${index}`}
                      label={`Franchise ${index + 1}`}
                      value={franchise}
                      onChange={(e) => handleFranchiseChange(index, e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Sélectionner une franchise</em>
                      </MenuItem>
                      {franchiseOptions.map((f) => (
                        <MenuItem key={f.id} value={f.id}>
                          {f.id} - {f.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton onClick={() => handleRemoveFranchise(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

              <Button
                className="secondaryDealActionButton"
                startIcon={<AddIcon />}
                onClick={handleAddFranchise}
                variant="outlined"
              >
                Ajouter une franchise
              </Button>
            </Grid>

            {/* Actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button className="secondaryDealActionButton" variant="outlined" onClick={() => navigate('/')}>
                  Annuler
                </Button>
                <Button className="dealActionButton" variant="contained" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <CircularProgress size={24} /> : 'Sauvegarder'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Prévisualisation - Par défaut */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Prévisualisation - Bannière par défaut
          </Typography>
          <Box
            sx={{
              backgroundColor: backgroundColor,
              borderTop: `2px solid ${borderColor}`,
              borderBottom: `2px solid ${borderColor}`,
              py: 3,
              px: 2,
            }}
          >
            {title && (
              <Typography
                variant="h5"
                sx={{ color: textColor, fontWeight: 700, textAlign: 'center', mb: 1.5, fontSize: '1.6rem' }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="h6"
                sx={{ color: textColor, fontWeight: 600, textAlign: 'center', mb: 1.5, fontSize: '1.28rem' }}
              >
                {subtitle}
              </Typography>
            )}
            {renderPreviewContent(content)}
            {renderPreviewFooter(footerText)}
          </Box>
        </CardContent>
      </Card>

      {/* Prévisualisation - Par franchise */}
      {franchiseBanners.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Prévisualisation - Bannières par franchise
            </Typography>
            {franchiseBanners.map((fb, index) => {
              if (!fb.franchise_id) return null;

              const previewTitle = fb.title.trim() || title;
              const previewSubtitle = fb.subtitle.trim() || subtitle;
              const previewContent = fb.content.trim() || content;
              const previewFooter = fb.footer_text.trim() || footerText;

              return (
                <Box key={index} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {getFranchise(fb.franchise_id)}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: backgroundColor,
                      borderTop: `2px solid ${borderColor}`,
                      borderBottom: `2px solid ${borderColor}`,
                      py: 3,
                      px: 2,
                    }}
                  >
                    {previewTitle && (
                      <Typography
                        variant="h5"
                        sx={{ color: textColor, fontWeight: 700, textAlign: 'center', mb: 1.5, fontSize: '1.6rem' }}
                      >
                        {previewTitle}
                      </Typography>
                    )}
                    {previewSubtitle && (
                      <Typography
                        variant="h6"
                        sx={{ color: textColor, fontWeight: 600, textAlign: 'center', mb: 1.5, fontSize: '1.28rem' }}
                      >
                        {previewSubtitle}
                      </Typography>
                    )}
                    {renderPreviewContent(previewContent)}
                    {renderPreviewFooter(previewFooter)}
                  </Box>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default PromotionalBannerAdminPage;
